<script>
import { mapActions, mapState } from 'vuex'
import ExpandableTextLine from './components/UI/ExpandableTextLine.vue'
// import Popper from '@/views/components/UI/Popper.js'
import PostFeed from './components/PostFeed'
import FeedSubscription from '../mixins/feed-subscription'
import PostView from './PostView'
import PageView from './PageView'
// import EditButtonBadge from './components/EditButtonBadge'
import { generateShortTitle, clearShortTitlesCache } from '@/lib/short-title.js'
import { comparePostsByDisplayOrder } from '@/lib/post-order.js'
import { db } from '@/lib/firebase'

export default {
  name: 'Home',
  mixins: [FeedSubscription],
  components: { PostFeed, PostView, ExpandableTextLine, PageView },

  data: () => ({
    orderBy: { field: 'date', direction: 'desc' },
    allowEdit: false,
    allowForward: true,
    allowBackward: true,
    downloadLink: '',
    postData: {},
    cvPost: {
      id: 'cv',
      title: 'CV',
      shortTitle: 'Cv',
      type: 'page'
    },
    loadingWorkId: null
  }),

  computed: {
    ...mapState(['user', 'navState']),
    post () {
      if (this.$route.name === 'work' && this.$route.params.id) {
        return this.posts.find(post => post.id === this.$route.params.id) || {}
      }
      if (this.$route.name === 'home') {
        return this.posts[Math.floor(Math.random() * this.posts.length)] || {}
      }
      if (this.$route.name === 'cv') {
        return this.cvPost
      }
      return {}
    },
    posts () {
      const posts = Object.values(this.feed).sort(comparePostsByDisplayOrder)
      clearShortTitlesCache()
      return [
        this.cvPost,
        ...posts.map(post => {
          const shortTitle = generateShortTitle(post.title)
          return { ...post, shortTitle }
        })
      ]
    },
    currentPostId () {
      return this.post.id
    },

    curIndex () {
      const index = this.posts.findIndex(post => post.id === this.post.id)
      return index === -1 ? 0 : index
    },

    shortDesc () {
      const tech = (this.post || {}).technique || ''
      const year = (this.post || {}).year || ''
      return `${tech}${tech && year ? ' | ' : ''}${year}`
    },
    prevLink () {
      return this.nextPostLink(-1)
    },

    nextLink () {
      return this.nextPostLink(1)
    },

    adminOrEditor () {
      return !!this.user && (this.user.role === 'admin' || this.user.role === 'editor')
    },

    workLoading () {
      return this.$route.name === 'work' &&
        !!this.$route.params.id &&
        !this.post.id &&
        (this.processing || this.loadingWorkId === this.$route.params.id)
    }
  },

  watch: {
    '$route.params.id' () {
      if (this.$route.params.id) {
        this.setNavState('off')
      }
      this.ensureWorkLoaded()
    },
    feed: {
      handler () {
        this.ensureWorkLoaded()
      },
      deep: true
    }
  },

  mounted () {
    if (!this.$route.params.id) {
      this.setNavState('on')
    } else {
      this.setNavState('off')
    }
    this.ensureWorkLoaded()
  },

  methods: {
    ...mapActions(['showEditor', 'setNavState']),
    toggleNavState () {
      const newState = this.navState === 'off' ? 'on' : 'off'
      this.setNavState(newState)
    },
    openEditor () {
      const func = (this.$refs.postView || {}).openEditor
      if (typeof func === 'function') {
        func()
      }
    },
    setPost (post) {
      this.postData = post || {}
    },
    setAllowEdit (val) {
      this.allowEdit = !!val
    },
    setDownloadLink (val) {
      this.downloadLink = val
    },
    goBackward () {
      this.goNext(-1)
    },

    goForward () {
      this.goNext(1)
    },

    goNext (step) {
      this.$router.push(this.nextPostLink(step))
    },

    nextPostLink (step) {
      const nextIndex = step > 0
        ? (this.curIndex + step) % this.posts.length
        : (this.curIndex - step + this.posts.length) % this.posts.length
      console.log('nextIndex', nextIndex)
      const nextPostId = (this.posts[nextIndex] || {}).id
      if (nextPostId === 'cv') {
        return { name: 'cv' }
      }
      return { name: 'work', params: { id: nextPostId } }
    },

    openEditorOld (value) {
      this.showEditor({ type: 'post', value })
    },
    titleLink (item) {
      return { name: 'post', params: { id: item.id } }
    },

    ensureWorkLoaded () {
      const id = this.$route.params.id
      if (this.$route.name !== 'work' || !id || this.feed[id] || this.loadingWorkId === id) return

      this.loadingWorkId = id
      db.collection('posts').doc(id).get()
        .then(doc => {
          if (!doc.exists) return
          const data = doc.data()
          const canView = data.status === 'public' ||
            (this.adminOrEditor && data.status === 'draft')
          if (canView) {
            this.$set(this.feed, doc.id, { ...data, id: doc.id })
          }
        })
        .finally(() => {
          if (this.loadingWorkId === id) this.loadingWorkId = null
        })
    }
  }
}
</script>

<template>
  <main>
    <page-view v-if="$route.name === 'cv'" ref="postView" />
    <post-view v-else-if="post.id" ref="postView" :post="post" />
    <div v-else-if="workLoading" class="work-loading">Loading...</div>
    <footer>
      <div class="work-title clickable" :class="navState">
        <button v-if="post && post.shortTitle" class="letter" @click="toggleNavState">
          <div>{{ post.shortTitle.charAt(0) }}<span>{{ post.shortTitle.slice(1) }}</span></div>
        </button>
        <expandable-text-line class="work-info" :duration="0.05">
          <h1 class="inline">
            {{ post.title }}
          </h1><span class=""> {{ shortDesc }}</span>
        </expandable-text-line>
      </div>
      <RouterLink :to="nextLink" class="button-next" title="Next work">
        <span />
      </RouterLink>
    </footer>
    <post-feed :posts="posts" :current-post-id="currentPostId" :processing="processing" class="flex-grow" />
  </main>
</template>

<style lang="scss">
:root {
  --footer-height: calc(var(--base-size) + var(--base-padding));
}

* {
  scroll-margin-top: var(--base-size);
  scroll-margin-bottom: var(--footer-height);
}

.work-loading {
  padding: var(--base-size) var(--small-padding);
  min-height: 50vh;
}

span.popper-trigger {
  display: block;
  height: 100%;
}

footer {
  position: fixed;
  width: 100%;
  height: var(--base-size);
  bottom: calc(var(--footer-height) - var(--base-size));
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  padding-left: var(--small-padding);
  padding-right: var(--small-padding);
  pointer-events: none;

  &>* {
    pointer-events: auto;
  }

  .work-title {
    position: relative;
    padding-right: var(--base-padding);
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: end;
    align-self: end;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      backdrop-filter: blur(5px);
      background-color: var(--color-bg-semitransparent);
      background-color: color-mix(in lab, var(--color-bg), transparent 20%);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.1s ease-in-out;
    }

    &.off {
      &:after {
        transition: opacity 0.8s 0.2s ease-in-out;
        opacity: 1;
      }
    }

    .work-info {
      padding-top: var(--small-padding);
      padding-bottom: var(--small-padding);
    }

    button.letter {
      position: relative;
      z-index: 1;
      font-size: 2rem;
      display: inline-block;
      padding: 0 var(--small-padding);
      text-decoration: underline;
      margin-right: var(--base-padding);

      span {
        font-size: 0.5em;
      }
    }
  }

  button, a.button-next {
    padding: 0;
    height: var(--base-size);
    width: var(--base-size);
    flex-shrink: 0;
    /* color: white; */

    /* &:hover, &:focus-visible {
      color: var(--color-aba-blue);
    } */
  }

  a.button-next span {
    --stroke: 0.25em;
    --s: 64%;
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;

    &:after {
      content: '';
      position: absolute;
      width: var(--s);
      height: var(--s);
      border: solid 0.2em currentColor;
      border-width: var(--stroke) var(--stroke) 0 0;
      transform: rotate(45deg);
    }
    &:before {
      content: '';
      position: absolute;
      display: block;
      width: calc(var(--s) * 1.1);
      height: var(--stroke);
      background-color: currentColor;
    }
  }
}
</style>
